import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "store";
import { useWindowDimensions } from "resources/BrowserManager";
import strings from "resources/strings";
import CONSTANTS from "shared/constants";
import ApiManager from "resources/ApiManager/api";
import {
  Flex,
  Space,
  Typography,
  Button,
  Modal,
  Alert,
  Tooltip,
  message,
  Card,
  theme,
} from "antd";
const { Text } = Typography;

import confetti from "canvas-confetti";

import { SyncOutlined, CrownFilled } from "@ant-design/icons";

import { RfcDateLastUpdateLabel } from "../../../routes/CFDI/components";

import { getNowUnix, getDateDifference } from "../../../shared/utilities";

import { getUserRfcs } from "components/Shared/RfcCard";

import FeatureTag from "components/Shared/FeatureTag";

import "./style.scss";
const styleWrapperClass = "modalRfcManualUpdate";

function ModalRfcManualUpdate({ type, showIcon, useSmallButton }) {
  const { token: theme_token } = theme.useToken();
  const { isSmallView } = useWindowDimensions();

  const navigate = useNavigate();

  const user = useStore((state) => state.user);
  const activeRfc = useStore((state) => state.activeRfc);
  const updateUserRfc = useStore((state) => state.updateUserRfc);

  const active_rfc_obj = user.rfcs[activeRfc];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHandlingRequest, setIsHandlingRequest] = useState(false);
  const [canUpdate, setCanUpdate] = useState("checking");
  const [timeToNextUpdate, setTimeToNextUpdate] = useState(null);

  const isUpdateJobRunning =
    !!active_rfc_obj?.date_last_update_requested_unix &&
    active_rfc_obj?.date_last_update_received_unix <
      active_rfc_obj?.date_last_update_requested_unix;

  const prevIsUpdateJobRunning = useRef(isUpdateJobRunning);

  const showFeedbackManualUpdateComplete = () => {
    message.success(strings.alertRfcManualUpdateComplete);

    setTimeout(
      () =>
        confetti({
          particleCount: 100,
          spread: 200,
          startVelocity: 25,
          decay: 0.9,
          origin: { x: 0.5, y: 0 },
          angle: 270,
          gravity: 0.8,
          colors: [theme_token.colorPrimary, theme_token.colorSecondary],
        }),
      150,
    );
  };

  useEffect(() => {
    if (prevIsUpdateJobRunning.current && !isUpdateJobRunning) {
      showFeedbackManualUpdateComplete();
    }
    prevIsUpdateJobRunning.current = isUpdateJobRunning;
  }, [isUpdateJobRunning]);

  const updateRfc = async () => {
    const rfcRes = await getUserRfcs(active_rfc_obj.rfc);
    if (rfcRes === -1) {
      return;
    }
    updateUserRfc(rfcRes[active_rfc_obj.rfc]);
  };

  useEffect(() => {
    if (!isUpdateJobRunning) return;

    const intervalId = setInterval(() => {
      updateRfc();
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isUpdateJobRunning]);

  if (
    active_rfc_obj.refresh_schedule !== "manual" &&
    !active_rfc_obj.plan_addons?.includes("cfdi_instant_refresh")
  ) {
    return;
  }

  const isRefreshStatusInactive = active_rfc_obj.refresh_status !== "active";

  const refreshUpdateTimeCheck = () => {
    if (!!!active_rfc_obj.date_last_update_received_unix) {
      setCanUpdate("initialLoadPending");
      return;
    }

    let timeWindowSeconds = null;
    if (active_rfc_obj.plan_name === "free") {
      timeWindowSeconds = 60 * 60 * 24 * 30;
    } else if (active_rfc_obj.plan_name === "basic") {
      timeWindowSeconds = 60 * 60 * 24 * 7;
    } else if (active_rfc_obj.plan_addons?.includes("cfdi_instant_refresh")) {
      timeWindowSeconds = 60 * 5;
    } else if (active_rfc_obj.plan_name === "pro") {
      timeWindowSeconds = 60 * 60 * 24 * 1;
    } else {
      return;
    }

    const nowUnix = getNowUnix();
    const lastUpdateUnix = active_rfc_obj.date_last_update_requested_unix || 0;
    const isOverThreshold = nowUnix - lastUpdateUnix >= timeWindowSeconds;

    if (isOverThreshold) {
      setCanUpdate("yes");
    } else {
      setCanUpdate("underThreshold");
      setTimeToNextUpdate(
        getDateDifference(lastUpdateUnix + timeWindowSeconds),
      );
    }
  };

  const showModal = () => {
    setIsModalOpen(true);
    refreshUpdateTimeCheck();
  };

  const handleCancel = () => {
    setIsHandlingRequest(false);
    setIsModalOpen(false);
  };

  const handleOk = async () => {
    //Call server
    setIsHandlingRequest(true);
    const apiRes = await ApiManager.postUserRfcSatManualUpdate(
      active_rfc_obj.rfc,
    );
    setIsHandlingRequest(false);

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    } else if (apiRes.response.code === 4006) {
      message.error(strings.alertRfcManualUpdateTimeoutWindowBlock);
      handleCancel();
      return;
    } else if (apiRes.response.code !== 2000) {
      message.error(strings.alertUnexpectedError);
      return;
    } else {
      message.success(strings.alertRfcManualUpdateSuccess);
    }

    //Update local variables and refresh
    updateUserRfc({
      ...active_rfc_obj,
      date_last_update_requested_unix: getNowUnix(),
    });

    //Close Modal
    handleCancel();
  };

  let description,
    descriptionUpgrade = null;
  if (active_rfc_obj.plan_name === "free") {
    description =
      "En el plan Demo, podrás actualizar tus CFDIs recibidos manualmente una vez al mes.";
    descriptionUpgrade =
      "¿Necesitas actualizar tus CFDIs más seguido? Cámbiate a uno de nuestros planes:";
  } else if (active_rfc_obj.plan_name === "basic") {
    description = active_rfc_obj.plan_addons?.includes("cfdi_instant_refresh")
      ? "En el plan Básico con actualizaciones inmediatas, podrás actualizar tus CFDIs recibidos manualmente una vez cada 5 minutos."
      : "En el plan Básico, podrás actualizar tus CFDIs recibidos manualmente una vez a la semana.";

    descriptionUpgrade = (
      <>
        ¿Necesitas actualizar tus CFDIs más seguido? Cámbiate al plan&nbsp;
        <CrownFilled className="fg-gold" /> Pro:
      </>
    );
  } else if (active_rfc_obj.plan_name === "pro") {
    description = active_rfc_obj.plan_addons?.includes(
      "cfdi_instant_refresh",
    ) ? (
      <>
        En el plan&nbsp;
        <CrownFilled className="fg-gold" /> Pro con actualizaciones inmediatas,
        podrás actualizar tus CFDIs recibidos y emitidos manualmente una vez
        cada 5 minutos.
      </>
    ) : (
      <>
        En el plan&nbsp;
        <CrownFilled className="fg-gold" /> Pro, podrás actualizar tus CFDIs
        recibidos y emitidos manualmente una vez al día.
      </>
    );
  }

  const ButtonRequestCfdi = () =>
    isSmallView && useSmallButton ? (
      <Flex vertical align="center" gap={8}>
        <Button
          type="primary"
          onClick={showModal}
          loading={isUpdateJobRunning}
          disabled={isRefreshStatusInactive}
          icon={
            showIcon && (
              <SyncOutlined
                spin={!isRefreshStatusInactive && isUpdateJobRunning}
              />
            )
          }
          size="middle"
          style={{
            borderRadius: "50%",
            width: 50,
            height: 50,
          }}
        />
        <Text
          style={{
            width: 65, //enough to show a full word
            textAlign: "center",
            whiteSpace: "normal",
            wordBreak: "normal",
            overflowWrap: "break-word",
            lineHeight: 1.2,
          }}
        >
          Actualizar CFDIs
        </Text>
      </Flex>
    ) : (
      <Button
        type="primary"
        onClick={showModal}
        loading={isUpdateJobRunning}
        disabled={isRefreshStatusInactive}
        icon={
          showIcon && (
            <SyncOutlined
              spin={!isRefreshStatusInactive && isUpdateJobRunning}
            />
          )
        }
      >
        Actualizar CFDIs
      </Button>
    );

  return (
    <div className={styleWrapperClass}>
      {isRefreshStatusInactive || isUpdateJobRunning ? (
        <Tooltip
          title={
            isRefreshStatusInactive
              ? "El RFC está inactivo y no se pueden recibir actualizaciones."
              : "¡Tus CFDIs se están actualizando!"
          }
          placement={isSmallView ? "bottomRight" : "top"}
        >
          {<ButtonRequestCfdi />}
        </Tooltip>
      ) : (
        <ButtonRequestCfdi />
      )}
      <Modal
        title="Actualizar CFDIs"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={860}
        destroyOnClose={true}
        footer={null}
        maskClosable={!isHandlingRequest}
        closable={!isHandlingRequest}
      >
        <Flex vertical gap={20}>
          <Text>{description}</Text>

          {active_rfc_obj.plan_addons?.includes("cfdi_instant_refresh") && (
            <Card styles={{ body: { padding: 15 } }}>
              <Flex vertical gap={10}>
                <Text>
                  <FeatureTag
                    type="feature_preview"
                    cursor="help"
                    tooltipTitle={
                      <Space direction="vertical">
                        <Text style={{ color: "white" }}>
                          Función disponible en versión preliminar antes de su
                          lanzamiento público.
                        </Text>
                        <Text style={{ color: "white", fontSize: "12px" }}>
                          * Las funciones o procedimientos pueden cambiar antes
                          de su lanzamiento oficial.
                        </Text>
                      </Space>
                    }
                  />
                  <span style={{ fontWeight: 600 }}>
                    ¡Actualiza manualmente tus CFDIs de manera inmediata!
                  </span>
                </Text>
                <Text>
                  Ahora podrás actualizar tus CFDIs al instante de manera
                  manual. Tus CFDIs se seguirán actualizando en su horario
                  regular según tu horario de actualización.
                </Text>
              </Flex>
            </Card>
          )}

          <RfcDateLastUpdateLabel type={type} active_rfc_obj={active_rfc_obj} />

          {canUpdate !== "yes" && (
            <Space direction="vertical" size={20}>
              <Alert
                message={
                  canUpdate === "underThreshold" ? (
                    <>
                      <Text>
                        Aún no transcurre suficiente tiempo desde tu útlima
                        actualización.
                      </Text>
                      <br />
                      <Text>
                        Podrás volver a actualizar tu información{" "}
                        <span style={{ fontWeight: "800" }}>
                          {timeToNextUpdate}
                        </span>
                        .
                      </Text>
                    </>
                  ) : canUpdate === "initialLoadPending" ? (
                    "Aún no se completa la carga inicial de tus CFDIs. Te notificaremos cuando estén listos."
                  ) : (
                    ""
                  )
                }
                type="warning"
                showIcon
              />

              {descriptionUpgrade && (
                <Flex
                  align={isSmallView ? "" : "center"}
                  vertical={isSmallView}
                  gap={8}
                >
                  <Text>{descriptionUpgrade}</Text>
                  <Button onClick={() => navigate("/plan")}>Ver Planes</Button>
                </Flex>
              )}
            </Space>
          )}
          <Flex vertical={isSmallView} justify="flex-end">
            <Button
              type="primary"
              loading={isHandlingRequest}
              onClick={handleOk}
              disabled={canUpdate !== "yes"}
              icon={<SyncOutlined />}
            >
              Actualizar CFDIs
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </div>
  );
}

export default ModalRfcManualUpdate;
